import { cellToLatLng } from "h3-js";
import { displayBucket } from "./display-bucket.js";

export type RawDepth = "off" | "shallow" | "medium" | "deep";

/** Firehose seq-budget for each backfill depth (how far back through the firehose to
 *  look; deeper = more bandwidth). 0 = live-only (no backfill). */
export function depthBudget(d: RawDepth): number {
  switch (d) {
    case "shallow": return 1_000;
    case "medium": return 10_000;
    case "deep": return 50_000;
    default: return 0;
  }
}

export interface RawReportPoint {
  id: string;
  cell: string;
  lat: number;
  lng: number;
  hazard: string;
  /** Raw CAP classification (for the report-detail panel + activity list). */
  category?: string;
  event?: string;
  severity?: string;
  responseType?: string;
  observedAt?: string;
  feed?: string;
}

/** Bounded fold of app.aid.report events into map points. Ring buffer keyed by report id
 *  (provenance.itemId, else the cell+seq): keeps the most-recent `capacity`, evicts oldest. */
export class RawReportStore {
  #byId = new Map<string, RawReportPoint>();
  readonly #cap: number;
  constructor(capacity = 2000) { this.#cap = capacity; }

  /** Returns true if the event was an app.aid.report and was stored. */
  ingest(evt: { seq: number; json: Record<string, unknown> }): boolean {
    if (evt.json?.["$type"] !== "app.aid.report") return false;
    const geo = (evt.json.geo ?? {}) as { cell?: string };
    const cell = geo.cell;
    if (!cell) return false;
    const p = (evt.json.payload ?? {}) as Record<string, unknown>;
    const prov = (p.provenance ?? {}) as { feed?: string; itemId?: string };
    const cls = (p.classification ?? {}) as { category?: string; event?: string; severity?: string; responseType?: string };
    const id = prov.itemId ?? `${cell}#${evt.seq}`;
    const [lat, lng] = cellToLatLng(cell);
    // Re-insert to move it to newest in iteration order (Map preserves insertion order).
    this.#byId.delete(id);
    this.#byId.set(id, {
      id, cell, lat, lng,
      hazard: displayBucket(cls.category, cls.event),
      category: cls.category,
      event: cls.event,
      severity: cls.severity,
      responseType: cls.responseType,
      observedAt: p.observedAt as string | undefined,
      feed: prov.feed,
    });
    while (this.#byId.size > this.#cap) {
      const oldest = this.#byId.keys().next().value as string;
      this.#byId.delete(oldest);
    }
    return true;
  }

  list(): RawReportPoint[] { return [...this.#byId.values()]; }
  clear(): void { this.#byId.clear(); }
  size(): number { return this.#byId.size; }
}

export interface RawReportFC {
  type: "FeatureCollection";
  features: Array<{
    type: "Feature";
    geometry: { type: "Point"; coordinates: [number, number] };
    properties: { id: string; hazard: string; observedAt?: string; feed?: string };
  }>;
}

export function rawReportsFC(points: RawReportPoint[]): RawReportFC {
  return {
    type: "FeatureCollection",
    features: points.map((p) => ({
      type: "Feature" as const,
      geometry: { type: "Point" as const, coordinates: [p.lng, p.lat] as [number, number] },
      properties: { id: p.id, hazard: p.hazard, observedAt: p.observedAt, feed: p.feed },
    })),
  };
}
